import {
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Spinner,
    Text,
    Button,
    ButtonGroup,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    useDisclosure,
} from '@chakra-ui/react';
import { FaIcon, ThemeTokens } from '@acp/core-ui';
import { Invoice, PropertyInvoice } from '@acp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { faExclamationTriangle, faCloudArrowUp } from '@fortawesome/pro-regular-svg-icons';
import TimeAgo from 'react-timeago';
import { format, isSameDay } from 'date-fns';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { useDownloadInvoicePdf } from '../../../hooks/use-invoice-pdf-download';
import { useInvoiceIntegrationUpload } from '../../../hooks/use-invoice-integration-upload';
import { ForwardInvoiceModal } from '../../forward-invoice-modal';

export function InvoiceRowActions({ invoice }: { invoice: Invoice | PropertyInvoice }) {
    const queryClient = useQueryClient();
    const downloadMutation = useDownloadInvoicePdf();
    const forwardInvoiceDisclosure = useDisclosure();

    const uploadMutation = useInvoiceIntegrationUpload({
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: ['dashboard', 'tab', 'overdue-invoices'],
            });
        },
    });

    return (
        <>
            {
                invoice.integration.isUploading && (
                    <Popover trigger="hover" isLazy>
                        <PopoverTrigger>
                            <Spinner size="sm" color="muted.400" />
                        </PopoverTrigger>
                        <PopoverContent>
                            <PopoverArrow />
                            <PopoverBody>
                                <Text fontSize="smaller">
                                    Syncing with your property management software
                                </Text>
                            </PopoverBody>
                        </PopoverContent>
                    </Popover>
                )
            }
            {
                !invoice.isPaid && invoice.integration.isUploaded && (
                    <Popover trigger="hover" isLazy>
                        <PopoverTrigger>
                            <FaIcon icon={faCloudArrowUp} color="muted.400" />
                        </PopoverTrigger>
                        <PopoverContent>
                            <PopoverArrow />
                            <PopoverBody>
                                <Text fontSize="smaller">
                                    Uploaded to your property management software
                                    {' '}
                                    {
                                        invoice.integration.uploadedAt !== null
                                        && (
                                            <span>
                                                {' '}
                                                {isSameDay(
                                                    new Date(),
                                                    invoice.integration.uploadedAt
                                                )
                                                    ? <TimeAgo date={invoice.integration.uploadedAt} title="" />
                                                    : `on ${format(invoice.integration.uploadedAt, 'dd/MM/yyyy')}`}
                                            </span>
                                        )
                                    }
                                    {
                                        invoice.integration.uploadedBy !== null
                                        && ` by ${invoice.integration.uploadedBy}`

                                    }
                                </Text>
                            </PopoverBody>
                        </PopoverContent>
                    </Popover>
                )
            }
            {
                invoice.integration.isEligibleToUpload
                && !invoice.integration.isUploading
                && invoice.integration.lastError !== null
                && (
                    <Popover trigger="hover" isLazy>
                        <PopoverTrigger>
                            <FaIcon icon={faExclamationTriangle} color="danger.400" />
                        </PopoverTrigger>
                        <PopoverContent>
                            <PopoverArrow />
                            <PopoverBody>
                                <Text fontSize="smaller">
                                    Failed to sync with your
                                    property management software (Error:
                                    {' '}
                                    {invoice.integration.lastError.message}
                                    )
                                </Text>
                            </PopoverBody>
                        </PopoverContent>
                    </Popover>
                )
            }
            <ButtonGroup size="sm" isAttached colorScheme="charcoal" variant="outline" justifySelf="end">
                {
                    invoice.integration.isEligibleToUpload
                    && (
                        <Button
                            borderRightWidth={1}
                            borderRightColor="secondary.900"
                            color="muted.700"
                            borderColor={ThemeTokens.Defaults.borderColor}
                            onClick={() => uploadMutation.upload({ invoice })}
                            isLoading={
                                uploadMutation.isPending
                                || uploadMutation.hasUploaded
                            }
                        >
                            {invoice.integration.lastError !== null ? 'Retry upload' : 'Upload'}
                        </Button>
                    )
                }
                {
                    !invoice.integration.isEligibleToUpload && (
                        <Button
                            borderRightWidth={1}
                            borderRightColor="secondary.900"
                            borderColor={ThemeTokens.Defaults.borderColor}
                            color="muted.700"
                            onClick={() => downloadMutation.download({ invoice })}
                        >
                            Download PDF
                        </Button>
                    )
                }

                <Menu size="large">
                    <MenuButton
                        as={IconButton}
                        borderStartRadius={0}
                        aria-label="Add to friends"
                        icon={<FaIcon icon={faChevronDown} />}
                        borderColor={ThemeTokens.Defaults.borderColor}
                        color="muted.700"
                    />
                    <MenuList>
                        {
                            invoice.integration.isEligibleToUpload && (
                                <MenuItem
                                    onClick={() => downloadMutation.download({ invoice })}
                                >
                                    Download PDF
                                </MenuItem>
                            )
                        }
                        <MenuItem onClick={forwardInvoiceDisclosure.onOpen}>
                            Send invoice
                        </MenuItem>
                    </MenuList>
                </Menu>
            </ButtonGroup>

            <ForwardInvoiceModal
                isOpen={forwardInvoiceDisclosure.isOpen}
                onClose={forwardInvoiceDisclosure.onClose}
                invoiceId={invoice.id}
                propertyId={invoice.property.compliancePropertyId}
            />
        </>
    );
}
